import React, { useContext, useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  CreateOutlined as CreateOutlinedIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  AddCircleRounded as AddCircleRoundedIcon,
  Cancel as CancelIcon,
  ExpandLess as ExpandLessIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AppDataContext from "../../common/appContext";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import moment from "moment";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

const useStyles = makeStyles((theme) => ({
  eventCardContainer: {
    overflow: "visible",
    maxWidth: "100%",
    flex: "0 0 auto",
    margin: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(0.2),
      marginRight: theme.spacing(0.2),
    },
  },
  NBABackground: {
    background: "#e0e7fa",
  },
  highlightCard: {
    border: "red solid 2px",
  },
  typoText: {
    color: "rgb(29, 66, 138)",
    fontWeight: "900",
  },
}));

const EventActions = ({
  event,
  expandedAccordions,
  handleAccordionChange,
  editEventCalendarDetails,
  executeEventDeleteAction,
  toggle,
  handleClick,
  isLargeScreen,
  user,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {!isLargeScreen ? (
        <ClickAwayListener onClickAway={() => handleClick(false)}>
          <div>
            {toggle && (
              <>
                {user && user.isAdmin && (
                  <>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => editEventCalendarDetails(event)}
                      >
                        <CreateOutlinedIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => executeEventDeleteAction(event.eventId)}
                      >
                        <DeleteIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </>
            )}
            <Tooltip title={!toggle ? "Add" : "Cancel"}>
              <IconButton onClick={() => handleClick(!toggle)}>
                {toggle ? (
                  <CancelIcon color="secondary" />
                ) : (
                  <AddCircleRoundedIcon color="primary" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                expandedAccordions.includes(event.eventId)
                  ? "Collapse Details"
                  : "Expand Details"
              }
            >
              <IconButton onClick={() => handleAccordionChange(event)}>
                {expandedAccordions.includes(event.eventId) ? (
                  <ExpandMoreIcon color="primary" fontSize="small" />
                ) : (
                  <VisibilityIcon color="primary" fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </div>
        </ClickAwayListener>
      ) : (
        <>
          {user && user.isAdmin && (
            <>
              <Tooltip title="Edit">
                <IconButton onClick={() => editEventCalendarDetails(event)}>
                  <CreateOutlinedIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => executeEventDeleteAction(event.eventId)}
                >
                  <DeleteIcon color="secondary" fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip
            title={
              expandedAccordions.includes(event.eventId)
                ? "Collapse Details"
                : "Expand Details"
            }
          >
            <IconButton onClick={() => handleAccordionChange(event)}>
              {expandedAccordions.includes(event.eventId) ? (
                <ExpandMoreIcon color="primary" fontSize="small" />
              ) : (
                <VisibilityIcon color="primary" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

const EventCard = ({
  event,
  selectedEvent,
  expandedAccordions,
  handleAccordionChange,
  editEventCalendarDetails,
  executeEventDeleteAction,
  participantsOpen,
  participantList,
  participantsCollapseChange,
  viewPlayerProfile,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const { user } = useContext(AppDataContext);
  const [toggle, setToggle] = useState(false);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const currentEventID = event.eventId;

  const newParticipant = participantList.find(
    (list) => list.eventId === String(event.participantEventId)
  );

  const filteredPlayers = newParticipant?.data?.filter((player) =>
    event.participants
      .split(",")
      .map((participant) => participant.trim())
      .includes(String(player.playerID).trim())
  );

  //console.log("card", { playerData, filteredPlayers });

  return (
    <Card
      id={`event-${event.eventId}`}
      key={event.eventId}
      className={`${classes.eventCardContainer} ${classes.NBABackground} ${
        selectedEvent.find((event) => event.eventId === currentEventID)
          ? classes.highlightCard
          : ""
      }`}
    >
      <CardContent
        sx={{
          "&:last-child": {
            paddingBottom: "0px",
          },
        }}
      >
        <Grid container spacing={1}>
          <Grid item lg={8} md={8} sm={6} xs={6}>
            <List sx={{ py: 0 }}>
              <ListItem alignItems="center" disableGutters sx={{ py: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      ...theme.typography.commonAvatar,
                      ...theme.typography.largeAvatar,
                      bgcolor: "#b0b0e6",
                      color: "#fff",
                    }}
                  >
                    🏀
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        noWrap
                        variant="subtitle2"
                        className={classes.typoText}
                        sx={{ mr: 1 }}
                      >
                        {event.title}
                      </Typography>
                      {!user.isCompleteTeamUser && (
                        <VerifiedIcon
                          fontSize="small"
                          sx={{
                            color: event.publish ? "green" : "text.secondary",
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.500", mt: 0.5 }}
                    >
                      {`Start Date : ${
                        moment(event.eventStartDateTime).isValid()
                          ? moment(event.eventStartDateTime).format(
                              "MM/DD/YYYY hh:mm A"
                            )
                          : "--"
                      } |  End Date : ${
                        moment(event.eventEndDateTime).isValid()
                          ? moment(event.eventEndDateTime).format(
                              "MM/DD/YYYY hh:mm A"
                            )
                          : "--"
                      }`}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Grid>

          <Grid
            item
            lg={4}
            md={4}
            sm={6}
            xs={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Box>
              <EventActions
                event={event}
                expandedAccordions={expandedAccordions}
                handleAccordionChange={handleAccordionChange}
                editEventCalendarDetails={editEventCalendarDetails}
                executeEventDeleteAction={executeEventDeleteAction}
                toggle={toggle}
                handleClick={setToggle}
                isLargeScreen={isLargeScreen}
                user={user}
              />
            </Box>
          </Grid>
        </Grid>

        <Collapse in={expandedAccordions.includes(event.eventId)} unmountOnExit>
          <Card sx={{ mb: "8px" }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Description:</strong> {event.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> {event.location}
                  </Typography>

                  {filteredPlayers?.length > 0 && (
                    <Grid item xs={12} pt={2}>
                      <Box
                        onClick={() => participantsCollapseChange(event)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          <strong>View Participants: </strong>
                        </Typography>
                        {participantsOpen.includes(event.eventId) ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </Box>
                      <Collapse
                        in={participantsOpen.includes(event.eventId)}
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {filteredPlayers?.map((participant, index) => (
                            <TitleTooltip title="View Profile" placement="top">
                              <Chip
                                key={index}
                                label={participant.playerName}
                                color="primary"
                                onClick={() =>
                                  viewPlayerProfile(
                                    event.participantEventId,
                                    participant.playerID
                                  )
                                }
                              />
                            </TitleTooltip>
                          ))}
                        </Box>
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default EventCard;
